import {
  getMemberTypeLabel,
  type MemberType,
  type PeopleFilter,
} from '@/components/People/member-type';
import type { PersonTeamBoolExp, RoleEnum } from '@/gql/scalars';
import {
  getOrganizationMemberAttributeValues,
  type OrganizationMemberBadgePreview,
  type OrganizationMembersBadgeAssignment,
  type OrganizationMembersSelectedBadgeAssignment,
} from '@/screens/OrganizationDetailMembers/OrganizationDetailMembers.utils';
import formatMembershipRoleLabel from '@/utils/formatMembershipRoleLabel';

const TEAM_MEMBERS_SEARCH_MINIMUM = 1;
const NO_TEAM_MEMBERS_CONTEXT_TEAM_ID = '00000000-0000-0000-0000-000000000000';
export const TEAM_MEMBER_TABLE_PREVIEW_LIMIT = 5;

export type TeamMembersPersonTeam = {
  member_type: MemberType;
  person_id: string;
  role?: RoleEnum | null;
  team_id: string;
  person: {
    avatar_id?: string | null;
    badge_assignments: OrganizationMembersBadgeAssignment[];
    color?: string | null;
    id: string;
    is_managed?: boolean | null;
    name?: string | null;
    selected_badge_assignments?: OrganizationMembersSelectedBadgeAssignment[];
  };
};

type TeamMemberRowData = {
  badges: string;
  badgePreviews: OrganizationMemberBadgePreview[];
  id: string;
  isManaged: boolean;
  memberType: MemberType;
  memberTypeLabel: string;
  personAvatarId: string | null;
  personColor: string | null;
  personId: string;
  personName: string;
  role: RoleEnum | null;
  roleLabel: string | null;
  selected: string | null;
  teamId: string;
};

export type TeamMemberRow = TeamMemberRowData &
  Record<
    string,
    number | string | boolean | null | OrganizationMemberBadgePreview[]
  >;

export function getTeamMembersPeopleWhere({
  badgeIds,
  memberType = 'participant',
  searchTerm,
  teamId,
}: {
  badgeIds: string[];
  memberType?: PeopleFilter;
  searchTerm: string;
  teamId?: string | null;
}): PersonTeamBoolExp {
  if (!teamId) {
    return {
      team_id: { _eq: NO_TEAM_MEMBERS_CONTEXT_TEAM_ID },
    };
  }

  const filters: PersonTeamBoolExp[] = [{ team_id: { _eq: teamId } }];

  if (memberType !== 'all') {
    filters.push({ member_type: { _eq: memberType } });
  }

  if (badgeIds.length) {
    filters.push({
      badge_assignments: {
        badge_id: { _in: badgeIds },
        team_id: { _eq: teamId },
      },
    });
  }

  if (searchTerm.length >= TEAM_MEMBERS_SEARCH_MINIMUM) {
    filters.push({
      person: {
        name: { _ilike: `%${searchTerm}%` },
      },
    });
  }

  return {
    _and: filters,
  };
}

export function toTeamMemberRow(
  personTeam: TeamMembersPersonTeam,
): TeamMemberRow {
  const person = personTeam.person;
  const selectedBadgeAssignment = person.selected_badge_assignments?.[0];
  const badgePreviews = person.badge_assignments.map((assignment) => ({
    color: assignment.badge.color ?? null,
    id: assignment.id,
    title: assignment.preview_value ?? assignment.badge.title ?? null,
  }));

  return {
    id: personTeam.person_id,
    badges: formatTeamMemberLabels(
      person.badge_assignments.map(
        (assignment) => assignment.preview_value ?? assignment.badge.title,
      ),
    ),
    badgePreviews,
    isManaged: Boolean(person.is_managed),
    memberType: personTeam.member_type,
    memberTypeLabel: getMemberTypeLabel(personTeam.member_type),
    personAvatarId: person.avatar_id ?? null,
    personColor: person.color ?? null,
    personId: person.id,
    personName: person.name ?? 'Unknown Member',
    role: personTeam.role ?? null,
    roleLabel: formatMembershipRoleLabel({
      isManaged: person.is_managed,
      role: personTeam.role,
    }),
    selected: null,
    teamId: personTeam.team_id,
    ...getOrganizationMemberAttributeValues(
      selectedBadgeAssignment?.badge_assignee_attributes ?? [],
    ),
  };
}

function formatTeamMemberLabels(labels: (string | null | undefined)[]) {
  const populatedLabels = labels.filter((label) => Boolean(label?.trim()));

  return populatedLabels.length ? populatedLabels.join(', ') : '-';
}
